import React, {useEffect, useRef, useState} from 'react';
import {
    downloadCSV,
    ExportButton,
    List,
    useGetList,
    useListContext,
    usePermissions,
    useRedirect,
    useTranslate,
} from 'react-admin';
import jsonExport from 'jsonexport/dist';
import {Alert, AlertTitle, Avatar, Box, MenuItem, Pagination, Select, Typography} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import {AddRounded} from '@mui/icons-material';
import {t} from '../themes/statamicTokens';
import PageHeader from '../components/statamic/PageHeader';
import PrimaryButton from '../components/statamic/PrimaryButton';
import SavedViewsTabs from '../components/statamic/SavedViewsTabs';
import FilterBar, {FilterDropdown} from '../components/statamic/FilterBar';
import ScopeBanner from '../components/statamic/ScopeBanner';
import useScopedFilter from '../scope/useScopedFilter';
import {useScope} from '../scope/ScopeContext';
import StatamicTable from '../components/statamic/StatamicTable';
import StatusDot from '../components/statamic/StatusDot';
import StatusPill from '../components/statamic/StatusPill';
import MonoChip from '../components/statamic/MonoChip';
import SectionCard from '../components/statamic/SectionCard';
import hasPermission from '../auth/hasPermission';
import {axiosApiInstance} from '../AxiosUtils';

const formatCurrency = (value, currency = 'EUR') => {
    if (value == null) return null;
    try {
        return new Intl.NumberFormat('en', {style: 'currency', currency}).format(value);
    } catch {
        return `${value} ${currency}`;
    }
};

const CharityCell = ({record}) => {
    const logoUrl = record.logo_name && record.ext
        ? `${process.env.REACT_APP_CDN_URL}/${record.logo_name}.${record.ext}`
        : undefined;

    return (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0}}>
            <Avatar
                src={logoUrl}
                variant="rounded"
                sx={{
                    width: 32,
                    height: 32,
                    fontSize: 13,
                    flexShrink: 0,
                    bgcolor: t.color.sidebarHover,
                    color: t.color.text.secondary,
                    border: `1px solid ${t.color.border.hairline}`,
                }}
            >
                {record.charity_name?.charAt(0)}
            </Avatar>
            <Box sx={{minWidth: 0}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <StatusDot variant={record.active ? 'success' : 'neutral'}/>
                    <Typography
                        sx={{
                            fontSize: `${t.type.bodyStrong.size}px`,
                            fontWeight: t.type.bodyStrong.weight,
                            color: t.color.text.primary,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {record.charity_name || 'Unnamed'}
                    </Typography>
                </Box>
                {record.parent_charity_name && (
                    <Typography
                        sx={{
                            mt: 0.25,
                            fontSize: `${t.type.caption.size}px`,
                            color: t.color.text.secondary,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {record.parent_charity_name}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

const NumberCell = ({value}) => (
    <Typography
        sx={{
            fontSize: `${t.type.body.size}px`,
            color: value == null ? t.color.text.muted : t.color.text.primary,
            fontVariantNumeric: 'tabular-nums',
            textAlign: 'right',
        }}
    >
        {value ?? '—'}
    </Typography>
);

const OvasImportPanel = () => {
    const translate = useTranslate();
    const {permissions} = usePermissions();
    const {umbrellaExternalId} = useScope();
    const isAdmin = permissions === 'admin';
    // NOTE: this checks umbrellaExternalId specifically (the v2/UUID scope
    // value), not umbrellaId. Right after picking an umbrella row, ScopeBanner
    // can already render as "scoped" off the numeric umbrellaId while the
    // external id is still resolving into scope storage — so this panel's own
    // missing-scope hint can transiently disagree with the banner until that
    // backfill lands. Self-corrects once umbrellaExternalId is set; not a bug.
    const missingAdminScope = isAdmin && !umbrellaExternalId;

    const [locationId, setLocationId] = useState('');
    const [importing, setImporting] = useState(false);
    const [status, setStatus] = useState(null);
    const [errorText, setErrorText] = useState('');

    const {data: locations} = useGetList(
        'locations',
        {
            filter: isAdmin ? {umbrella_id: umbrellaExternalId} : {},
            sort: {field: 'title', order: 'ASC'},
        },
        {enabled: !missingAdminScope},
    );

    // Derive choices directly from `locations` — no state mirror, so a scope
    // change can't leave a stale list of the PREVIOUS umbrella's locations
    // sitting in local state after `locations` itself has updated.
    const locationChoices = (locations || []).map((l) => ({id: l.id, name: l.title}));

    // Reset the selected location whenever the admin's umbrella scope actually
    // changes — a location id from the OLD umbrella must not silently ride
    // along into an import request scoped to the new one.
    const prevUmbrellaExternalId = useRef(umbrellaExternalId);
    useEffect(() => {
        if (prevUmbrellaExternalId.current !== umbrellaExternalId) {
            setLocationId('');
        }
        prevUmbrellaExternalId.current = umbrellaExternalId;
    }, [umbrellaExternalId]);

    const importData = () => {
        setImporting(true);
        setStatus(null);
        setErrorText('');
        axiosApiInstance.post(
            `${process.env.REACT_APP_API_URL}/api/v1/umbrellad/importData/${locationId}`
        ).then(() => {
            setImporting(false);
            setStatus('success');
        }).catch((reason) => {
            setImporting(false);
            setStatus('error');
            setErrorText(String(reason));
        });
    };

    // Admins see the select as read-only context (no v2 admin import target
    // yet — see the muted hint below), so their visibility isn't gated on
    // having loadable choices the way the umbrella-role flow is.
    if (!isAdmin && !locationChoices.length) return null;

    return (
        <Box sx={{mx: 3, mb: 3}}>
            <SectionCard
                title="Import from OVAS"
                subtitle="Import charity data for a project location"
            >
                <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
                    <Select
                        value={locationId}
                        displayEmpty
                        disabled={isAdmin}
                        onChange={(e) => setLocationId(e.target.value)}
                        sx={{
                            minWidth: 220,
                            fontSize: `${t.type.body.size}px`,
                            backgroundColor: t.color.surface,
                            '& .MuiOutlinedInput-notchedOutline': {borderColor: t.color.border.default},
                        }}
                    >
                        <MenuItem value="" disabled>
                            <span style={{color: t.color.text.muted}}>
                                {missingAdminScope ? translate('Select an umbrella scope first') : 'Select location…'}
                            </span>
                        </MenuItem>
                        {locationChoices.map((loc) => (
                            <MenuItem key={loc.id} value={loc.id}>{loc.name}</MenuItem>
                        ))}
                    </Select>
                    {!isAdmin && locationId > 0 && (
                        <LoadingButton
                            onClick={importData}
                            loading={importing}
                            variant="outlined"
                            size="small"
                            sx={{
                                textTransform: 'none',
                                fontSize: `${t.type.small.size}px`,
                                borderColor: t.color.border.default,
                                color: t.color.text.primary,
                                '&:hover': {borderColor: t.color.accent.primary, color: t.color.accent.primary, backgroundColor: 'transparent'},
                            }}
                        >
                            {translate('Import')}
                        </LoadingButton>
                    )}
                    {isAdmin && (
                        <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.muted, width: '100%'}}>
                            {translate('Import for admins lands with the v2 utilities vertical')}
                        </Typography>
                    )}
                </Box>
                {status === 'success' && (
                    <Alert severity="success" sx={{mt: 2, fontSize: `${t.type.small.size}px`}}>
                        <AlertTitle sx={{fontSize: `${t.type.body.size}px`}}>Success</AlertTitle>
                        Data import complete.
                    </Alert>
                )}
                {status === 'error' && (
                    <Alert severity="warning" sx={{mt: 2, fontSize: `${t.type.small.size}px`}}>
                        <AlertTitle sx={{fontSize: `${t.type.body.size}px`}}>Warning</AlertTitle>
                        {errorText}
                    </Alert>
                )}
            </SectionCard>
        </Box>
    );
};

const CharityListStatamicInner = () => {
    const redirect = useRedirect();
    const translate = useTranslate();
    const {permissions} = usePermissions();
    const {filterValues, setFilters, displayedFilters} = useListContext();
    const [activeViewId, setActiveViewId] = useState('all');
    useScopedFilter('v2Charities');

    const canCreate = hasPermission(permissions, ['create'], 'charities');
    const canSeeUmbrellaBits = hasPermission(permissions, ['view'], 'umbrella');
    const canSeeImport = hasPermission(permissions, ['show'], 'charities');

    const views = [
        {id: 'all',    label: translate('All')},
        {id: 'active', label: translate('Active')},
        ...(canSeeUmbrellaBits ? [{id: 'demo', label: 'Demo'}] : []),
    ];

    const handleViewSelect = (id) => {
        setActiveViewId(id);
        const base = {...filterValues};
        delete base.active;
        delete base.demo_charity;
        if (id === 'active')    setFilters({...base, active: true}, displayedFilters);
        else if (id === 'demo') setFilters({...base, demo_charity: true}, displayedFilters);
        else                    setFilters(base, displayedFilters);
    };

    const rowActions = (record) => [
        {label: translate('View'), onClick: () => redirect('show', 'charities', record.id)},
        {label: translate('Edit'), onClick: () => redirect('edit', 'charities', record.id)},
    ];

    const columns = [
        {
            key: 'name', label: translate('Name'), sortable: true, sortField: 'charity_name',
            render: (r) => <CharityCell record={r}/>,
        },
        {
            key: 'location', label: translate('Location'), width: 180,
            render: (r) => (
                <Typography sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.secondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                    {[r.city, r.country].filter(Boolean).join(', ') || '—'}
                </Typography>
            ),
        },
        {
            key: 'sponsorship', label: translate('Sponsorship amount'), width: 150,
            render: (r) => (
                <Typography sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.primary, fontVariantNumeric: 'tabular-nums', textAlign: 'right'}}>
                    {formatCurrency(r.sponsorship_amount, r.default_currency) ?? '—'}
                </Typography>
            ),
        },
        {
            key: 'children', label: '# children', width: 100,
            render: (r) => <NumberCell value={r.nr_of_children}/>,
        },
        {
            key: 'sponsored', label: '# sponsored', width: 110,
            render: (r) => <NumberCell value={r.nr_of_children_sponsored}/>,
        },
        {
            key: 'volunteers', label: '# volunteers', width: 110,
            render: (r) => <NumberCell value={r.nr_of_volunteers}/>,
        },
        {
            key: 'payment', label: translate('Payment'), width: 130,
            render: (r) => r.payment_link_url
                ? <MonoChip>configured</MonoChip>
                : <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.muted}}>not set</Typography>,
        },
        {
            key: 'status', label: translate('Status'), width: 110,
            render: (r) => r.demo_charity
                ? <StatusPill variant="info">Demo</StatusPill>
                : <StatusPill variant={r.active ? 'success' : 'neutral'}>{r.active ? 'Active' : 'Inactive'}</StatusPill>,
        },
    ];

    const updateFilter = (src, v) => setFilters({...filterValues, [src]: v}, displayedFilters);
    const clearFilter = (src) => {
        const next = {...filterValues};
        delete next[src];
        setFilters(next, displayedFilters);
    };

    return (
        <Box sx={{backgroundColor: t.color.page, minHeight: '100%'}}>
            <PageHeader
                title={translate('Charities')}
                subtitle={translate('Charity organizations and their projects')}
                actions={
                    <>
                        <ExportButton label={translate('Export')}/>
                        {canCreate && (
                            <PrimaryButton
                                variant="primary"
                                startIcon={<AddRounded sx={{fontSize: 18}}/>}
                                onClick={() => redirect('create', 'charities')}
                            >
                                {translate('Create charity')}
                            </PrimaryButton>
                        )}
                    </>
                }
            />

            <Box
                sx={{
                    backgroundColor: t.color.surface,
                    border: `1px solid ${t.color.border.default}`,
                    borderRadius: `${t.radius.md}px`,
                    mx: 3,
                    mb: 3,
                    overflow: 'hidden',
                }}
            >
                <ScopeBanner/>
                <SavedViewsTabs
                    views={views}
                    activeId={activeViewId}
                    onSelect={handleViewSelect}
                    onAdd={() => {}}
                />

                <FilterBar
                    searchValue={filterValues?.q}
                    onSearchChange={(q) => setFilters({...filterValues, q}, displayedFilters)}
                    placeholder={translate('Search charities')}
                    filters={
                        <>
                            <FilterDropdown
                                label={translate('Active')}
                                source="active"
                                type="boolean"
                                value={filterValues?.active}
                                onChange={updateFilter}
                                onClear={clearFilter}
                            />
                            {canSeeUmbrellaBits && (
                                <FilterDropdown
                                    label="Demo"
                                    source="demo_charity"
                                    type="boolean"
                                    value={filterValues?.demo_charity}
                                    onChange={updateFilter}
                                    onClear={clearFilter}
                                />
                            )}
                        </>
                    }
                />

                <StatamicTable
                    columns={columns}
                    rowActions={rowActions}
                    onRowClick={(record) => redirect('show', 'charities', record.id)}
                />

                <StatamicPagination/>
            </Box>

            {canSeeImport && <OvasImportPanel/>}
        </Box>
    );
};

const StatamicPagination = () => {
    const {page, perPage, total, setPage} = useListContext();
    const pageCount = Math.max(1, Math.ceil((total ?? 0) / (perPage ?? 25)));
    if (pageCount <= 1) return null;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 3,
                py: 1.5,
                borderTop: `1px solid ${t.color.border.hairline}`,
            }}
        >
            <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.muted}}>
                Showing page {page} of {pageCount} · {total ?? 0} total
            </Typography>
            <Pagination
                count={pageCount}
                page={page}
                onChange={(_, p) => setPage(p)}
                size="small"
                shape="rounded"
                sx={{
                    '& .MuiPaginationItem-root': {
                        fontSize: `${t.type.caption.size}px`,
                        color: t.color.text.secondary,
                        borderColor: t.color.border.default,
                    },
                    '& .Mui-selected': {
                        backgroundColor: `${t.color.accent.primaryTint} !important`,
                        color: `${t.color.accent.primary} !important`,
                    },
                }}
            />
        </Box>
    );
};

const charityExporter = (dataset) => {
    const rows = dataset.map((item) => {
        const {backlinks, _links, translations, ...rest} = item;
        return rest;
    });
    jsonExport(rows, {headers: ['id', 'charity_name']}, (err, csv) => {
        downloadCSV(csv, 'projecten');
    });
};

const CharityListStatamic = (props) => (
    <List
        {...props}
        sort={{field: 'charity_name', order: 'ASC'}}
        perPage={25}
        actions={false}
        exporter={charityExporter}
        pagination={false}
        component={Box}
        sx={{
            backgroundColor: t.color.page,
            '& .RaList-main': {backgroundColor: t.color.page},
        }}
    >
        <CharityListStatamicInner/>
    </List>
);

export default CharityListStatamic;
